import React, { useRef, useState } from "react";
import isEqual from "react-fast-compare";
import { ReactTFC } from "../../../../../../utils/fp/missing-ts-parts";

import { DelayRendering, SmallLoadingSpinner } from "../../../../../../routes/modal-loading-spinner";
import { CheckoutPaymentMethodProps } from "../../adyen/types";
import { useConfiguredAdyenInstance } from "../../adyen/use-adyen-instance";
import {
  CheckoutPaymentMethodSelectionState_ApplePay,
  CheckoutPaymentMethodSelectionState_Eps,
  CheckoutPaymentMethodSelectionState_GooglePay,
  CheckoutPaymentMethodSelectionState_Ideal,
  PaymentMethodTag,
} from "../../state/payment-methods";
import { CheckoutPaymentMethodWithAdyen } from "./CheckoutPaymentMethodWithAdyen";
import { GooglePayData } from "./GooglePayData";
import { mapOpenedPaymentMethodToState } from "./mapOpenedPaymentMethodToState";
import { mapStateToInitOpenedPaymentMethod } from "./mapStateToInitOpenedPaymentMethod";
import { CCState } from "./types";
import { useApplePayAvailability } from "./use-apple-pay-availability";

type Selected<Data> = { tag: "selected"; data: Data };
type InitOrSelected<Data> = { tag: "initialized" } | Selected<Data>;
type TaggedWithScreen<Tag extends string, Data> = { tag: Tag; screen: InitOrSelected<Data> };
type TaggedWithJustSelectedScreen<Tag extends string, Data> = { tag: Tag; screen: Selected<Data> };

export type OpenedCreditCardPaymentMethod = {
  tag: "credit-card";
  screen: CCState;
};

export type OpenedCheckoutApplePay = TaggedWithScreen<"applepay", CheckoutPaymentMethodSelectionState_ApplePay["data"]>;
export type OpenedCheckoutGooglePay = TaggedWithJustSelectedScreen<
  "googlepay",
  CheckoutPaymentMethodSelectionState_GooglePay["data"]
>;
export type OpenedCheckoutEps = TaggedWithScreen<"eps", CheckoutPaymentMethodSelectionState_Eps["data"]>;

type NoDataPaymentMethodTag = Exclude<PaymentMethodTag, "credit-card" | "eps" | "applepay" | "googlepay">;

export type OpenedPaymentMethod =
  | OpenedCreditCardPaymentMethod
  | OpenedCheckoutApplePay
  | OpenedCheckoutGooglePay
  | OpenedCheckoutEps
  | { tag: NoDataPaymentMethodTag }
  | null;

const CheckoutPaymentMethod_: ReactTFC<CheckoutPaymentMethodProps> = props => {
  const [openedPaymentMethod, setOpenedPaymentMethod_] = useState<OpenedPaymentMethod>(() =>
    mapStateToInitOpenedPaymentMethod(props.currentCartPaymentMethod),
  );

  function setOpenedPaymentMethod(d: OpenedPaymentMethod) {
    setOpenedPaymentMethod_(d);

    void props.onCurrentCartPaymentMethodChanged(mapOpenedPaymentMethodToState(d));
  }

  const isApplePayAvailableOnThisDevice = useApplePayAvailability();

  const handleOnChange = useRef<(state: unknown, component: unknown) => void>((state, component) => {
    const googlePayState: GooglePayData = state as any;

    if (googlePayState?.data?.paymentMethod?.type === "googlepay") {
      if (googlePayState?.isValid) {
        setOpenedPaymentMethod({ tag: "googlepay", screen: { tag: "selected", data: googlePayState.data } });
      } else {
        setOpenedPaymentMethod(null);
      }
    }
  });

  const adyenInstance = useConfiguredAdyenInstance((state, component) => {
    // console.log(state, component);
  }, handleOnChange.current);

  if (!adyenInstance) {
    return (
      <DelayRendering delayMs={300}>
        <SmallLoadingSpinner size={"2x"} color={"green"} />
      </DelayRendering>
    );
  }

  const loadedAdyenInstance = adyenInstance.underlying;

  return (
    <CheckoutPaymentMethodWithAdyen
      {...props}
      loadedAdyenInstance={loadedAdyenInstance}
      openedPaymentMethod={openedPaymentMethod}
      isApplePayAvailableOnThisDevice={isApplePayAvailableOnThisDevice}
      setOpenedPaymentMethod={setOpenedPaymentMethod}
    />
  );
};

export const CheckoutPaymentMethod = React.memo(CheckoutPaymentMethod_, isEqual);

// // @ts-expect-error
// CheckoutPaymentMethod.whyDidYouRender = true;
